import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Globe, Users, MapPin } from 'lucide-react';

const HighlightCard = ({ icon: Icon, label, sublabel, index }) => {
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1, ease: [0.25, 0.1, 0.25, 1.0] }}
            className="flex items-start gap-4 p-5 rounded-xl bg-surface border border-border hover:border-primary/30 hover:shadow-glow-sm transition-all duration-500 group"
        >
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-300">
                <Icon size={20} />
            </div>
            <div>
                <h3 className="text-text font-bold font-display text-lg leading-tight mb-1 group-hover:text-primary transition-colors">
                    {label}
                </h3>
                <p className="text-text-tertiary text-xs font-mono">
                    {sublabel}
                </p>
            </div>
        </motion.div>
    );
};

const AboutV3 = ({ data }) => {
    const elevatorPitch = data.bio.split('.')[0] + '.';

    const highlights = [
        {
            icon: TrendingUp,
            label: "Promoted in 8 Months",
            sublabel: "Associate to Analyst at Pebl"
        },
        {
            icon: Globe,
            label: "Trilingual Support",
            sublabel: "English / Spanish / Russian"
        },
        {
            icon: MapPin,
            label: "4 Global Regions",
            sublabel: "LATAM, EMEA, APAC, Americas"
        },
        {
            icon: Users,
            label: "200+ Core Users",
            sublabel: "DevRev Platform Admin"
        }
    ];

    return (
        <section id="about" className="py-24 md:py-32 bg-background-alt relative overflow-hidden">
            {/* Background Decoration */}
            <div className="absolute right-0 top-1/4 w-1/3 h-1/2 bg-gradient-to-b from-primary/3 to-transparent blur-[120px] pointer-events-none" />

            <div className="max-w-6xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">

                    {/* Left Column: Text & Availability */}
                    <div className="lg:col-span-5 flex flex-col gap-8">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <span className="text-primary font-mono text-sm tracking-wider uppercase mb-3 block">
                                About Me
                            </span>
                            <h2 className="text-3xl md:text-4xl font-display font-bold text-text mb-6">
                                Building reliable systems<br />at global scale
                            </h2>
                            <p className="text-text-secondary text-lg leading-relaxed mb-6">
                                {elevatorPitch}
                            </p>
                            <p className="text-text-secondary text-lg leading-relaxed">
                                Currently serving as Systems Support Analyst at Pebl, where I own the DevRev platform end-to-end.
                            </p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="p-4 rounded-xl border border-primary/20 bg-primary/5 backdrop-blur-sm"
                        >
                            <div className="flex items-center gap-3 mb-2">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
                                </span>
                                <span className="text-primary font-mono text-xs font-bold uppercase tracking-wider">
                                    Current Status
                                </span>
                            </div>
                            <p className="text-sm text-text-secondary">
                                Open to Platform Support Engineer, Cloud Support Engineer, and Technical Support Engineer roles.
                            </p>
                        </motion.div>
                    </div>

                    {/* Right Column: Highlights Grid */}
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {highlights.map((item, index) => (
                                <HighlightCard key={index} {...item} index={index} />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutV3;
